<template>
  <template v-if="benefits.length">
    <Divider />

    <div class="space-y-4">
      <h2>{{ t('benefits') }}</h2>

      <div class="space-y-2">
        <div
          v-for="(benefit, i) in benefits"
          :key="`${benefit.couponCode}-${i}`"
          class="rounded-md bg-white px-4 py-3"
        >
          <div class="flex flex-wrap items-center justify-between gap-3">
            <div class="flex flex-wrap items-center gap-2">
              <span class="text-sm font-medium">{{ formatBenefit(benefit.extra) }}</span>
              <Badge v-if="benefit.code" variant="neutral" class="font-mono">
                {{ benefit.code }}
              </Badge>
            </div>
            <span
              v-if="benefit.extra.customVehicleExtraType === VehicleExtraType.PRICE_REDUCTION"
              class="text-sm tabular-nums text-obsidian-700"
            >
              <span v-currency="benefit.extra.price" />
            </span>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script lang="ts" setup>
import { getAllCustomBookedExtras } from '@/utils/pricing.utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Badge from '@/components/Badge.vue';
import Divider from '@/components/Divider.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { CustomBookedExtra } from '@/entities/bookings/booked-extra';
import { VehicleExtraType } from '@carvia/ros-client-types';

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();

const benefits = computed(() => {
  const extras =
    getAllCustomBookedExtras(props.booking.priceCalculation)?.filter(
      (extra) => extra.isAdditional,
    ) ?? [];
  return extras.map((extra) => ({
    extra,
    couponCode: extra.couponCode ?? '',
    code: extra.couponCode,
  }));
});

const formatBenefit = (extra: CustomBookedExtra) => {
  const typeLabel = extra.customVehicleExtraType
    ? t(`vehicleExtras.${extra.customVehicleExtraType}`)
    : extra.description;
  if (extra.customVehicleExtraType === VehicleExtraType.EXTRA_KM) {
    return `${typeLabel}: +${extra.value ?? 0} km`;
  }
  if (extra.customVehicleExtraType === VehicleExtraType.PRICE_REDUCTION) {
    return typeLabel;
  }
  return `${typeLabel}${extra.value != null ? `: ${extra.value}` : ''}`;
};
</script>

<i18n lang="json">
{
  "en": {
    "benefits": "Coupon Benefits"
  },
  "de": {
    "benefits": "Gutschein-Vorteile"
  },
  "es": {
    "benefits": "Beneficios del cupón"
  }
}
</i18n>
